import React, { useState, useEffect } from 'react';
import { LanguageCode, DonationRecord, SiteSettings, MemberUser } from '../types';
import { translations } from '../i18n/translations';
import {
  HeartHandshake,
  CreditCard,
  ShieldCheck,
  Download,
  CheckCircle2,
  Building,
  RefreshCw,
  Lock,
  ArrowRight,
  Info,
  Check,
  Smartphone,
  ChevronDown,
  X,
  FileText,
  BadgeCheck,
  ShieldAlert,
  Copy,
  DollarSign,
  User,
  ShieldX,
  Clock,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { createDonationApi } from '../services/api';

interface DonateViewProps {
  currentLang: LanguageCode;
  currentUser?: MemberUser | null;
  onOpenAuthModal?: () => void;
  settings?: SiteSettings;
  onDonationSuccess?: (donation: DonationRecord) => void;
  onShowToast?: (message: string, type?: 'success' | 'info' | 'warning', title?: string) => void;
}

export const DonateView: React.FC<DonateViewProps> = ({
  currentLang,
  currentUser,
  onOpenAuthModal,
  settings,
  onDonationSuccess,
  onShowToast,
}) => {
  const t = translations[currentLang];
  
  // Dynamic Campaigns list from settings or defaults
  const campaignsList = settings?.donationCampaigns && settings.donationCampaigns.length > 0
    ? settings.donationCampaigns.filter(c => c.isActive)
    : [
        {
          id: 'camp-1',
          title: 'Dil Kursları ve Kültür Evi Destek Fonu',
          category: 'Eğitim & Dil',
          description: 'Derneğimizde verilen Kürtçe dil dersleri, öğretmen maaşları, ders materyalleri ve kültür evi kira & işletme giderlerini karşılar.',
          targetAmount: 20000,
          collectedAmount: 14500,
          isActive: true,
        },
        {
          id: 'camp-2',
          title: 'Öğrenci Burs Fonu',
          category: 'Eğitim Yardım',
          description: 'Yurtdışında ve bölgede üniversite okuyan ihtiyaç sahibi 50+ öğrenciye her ay düzenli eğitim bursu desteği sağlar.',
          targetAmount: 15000,
          collectedAmount: 11200,
          isActive: true,
        },
        {
          id: 'camp-3',
          title: 'Newroz Festivali Kültür Sponsorluğu',
          category: 'Kültür & Etkinlik',
          description: 'Geleneksel Newroz kutlaması, sahne kurulumu, dengbêj ve sanatçı ağırlama, halk oyunları kostümleri ve organizasyon fonu.',
          targetAmount: 10000,
          collectedAmount: 8900,
          isActive: true,
        },
        {
          id: 'camp-4',
          title: 'İnsani Yardım ve Acil Afet Fonu',
          category: 'Acil Yardım',
          description: 'Doğal afetler, acil insani krizler ve dayanışma ihtiyacı duyan aileler için anında devredilen acil destek fonu.',
          targetAmount: 30000,
          collectedAmount: 19750,
          isActive: true,
        },
      ];

  // Donation Type: 'one_time' or 'recurring'
  const [donationType, setDonationType] = useState<'one_time' | 'recurring'>('one_time');

  // Amount state
  const [selectedAmount, setSelectedAmount] = useState<number>(50);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [campaign, setCampaign] = useState<string>(campaignsList[0]?.title || 'Dil Kursları ve Kültür Evi Destek Fonu');
  const [paymentMethod, setPaymentMethod] = useState<'pos' | 'sepa'>('pos');
  const [isSuccess, setIsSuccess] = useState(false);
  const [copiedIban, setCopiedIban] = useState(false);

  // Selected Campaign Details
  const selectedCampaignObj = campaignsList.find(c => c.title === campaign) || campaignsList[0];

  // POS & SEPA Details from settings
  const posConfig = settings?.paymentGateway;
  const associationIban = posConfig?.iban || 'FR76 3000 4000 0123 4567 8901 234';
  const associationSwift = posConfig?.swiftCode || 'AGRIFRPPXXX';
  const associationAccountName = posConfig?.bankAccountName || 'Kürt Kültür ve Dayanışma Derneği Yvelines';

  const [debitDayOfMonth, setDebitDayOfMonth] = useState<number>(1); // 1st of month
  const [identityNumber, setIdentityNumber] = useState<string>('38291029184');

  // Donor Details
  const [donorName, setDonorName] = useState(currentUser?.fullName || 'Deniz Yılmaz');
  const [donorEmail, setDonorEmail] = useState(currentUser?.email || 'deniz@example.com');
  const [donorPhone, setDonorPhone] = useState(currentUser?.phone || '+33 6 12 34 56 78');
  const [cardHolderName, setCardHolderName] = useState(currentUser?.fullName || 'Deniz Yılmaz');
  const [cardNumber, setCardNumber] = useState('4543 8812 9940 3312');
  const [cardExp, setCardExp] = useState('12/28');
  const [cardCvc, setCardCvc] = useState('321');
  const [securityErrorMsg, setSecurityErrorMsg] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Auto-sync form state when logged in user changes
  useEffect(() => {
    if (currentUser) {
      setDonorName(currentUser.fullName);
      setCardHolderName(currentUser.fullName);
      if (currentUser.email) setDonorEmail(currentUser.email);
      if (currentUser.phone) setDonorPhone(currentUser.phone);
    }
  }, [currentUser]);

  // POS 3D Secure Verification Modal State
  const [is3DSecureModalOpen, setIs3DSecureModalOpen] = useState(false);
  const [posStep, setPosStep] = useState<'processing' | 'otp' | 'success'>('processing');
  const [otpCode, setOtpCode] = useState('');
  const [isInfoGuideOpen, setIsInfoGuideOpen] = useState(false);

  const quickAmounts = [10, 25, 50, 100, 250];

  const normalizeNameForComparison = (s: string) => {
    if (!s) return '';
    return s
      .trim()
      .replace(/İ/g, 'I')
      .replace(/ı/g, 'I')
      .replace(/Ş/g, 'S')
      .replace(/ş/g, 'S')
      .replace(/Ğ/g, 'G')
      .replace(/ğ/g, 'G')
      .replace(/Ü/g, 'U')
      .replace(/ü/g, 'U')
      .replace(/Ö/g, 'O')
      .replace(/ö/g, 'O')
      .replace(/Ç/g, 'C')
      .replace(/ç/g, 'C')
      .toUpperCase()
      .replace(/\s+/g, ' ');
  };

  const handleCopyIban = () => {
    navigator.clipboard.writeText(associationIban.replace(/\s+/g, ''));
    setCopiedIban(true);
    setTimeout(() => setCopiedIban(false), 2000);
  };

  // Submit Handler
  const handleDonateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSecurityErrorMsg('');

    // Security Check: Must be logged in member
    if (!currentUser) {
      setSecurityErrorMsg('🔒 GÜVENLİK UYARISI: Bağış yapabilmek için dernek üyesi olmanız ve sisteme giriş yapmanız zorunludur.');
      return;
    }

    const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;
    if (!finalAmount || finalAmount <= 0) {
      alert('Lütfen geçerli bir tutar giriniz.');
      return;
    }

    if (!donorName.trim()) {
      setSecurityErrorMsg('Lütfen kimliğinizdeki ad ve soyadınızı giriniz.');
      return;
    }

    // 🔒 CLIENT-SIDE NAME CROSS-REFERENCE CHECK: Donor Name vs Verified Member Name
    const normMember = normalizeNameForComparison(currentUser.fullName);
    const normDonor = normalizeNameForComparison(donorName);

    if (normDonor !== normMember) {
      const mismatchMsg = `🚨 GÜVENLİK İHLALİ VE ÖDEME REDDİ: Bağış formuna yazılan ad soyad ("${donorName.toUpperCase()}") ile dernek sisteminde kayıtlı resmi onaylı üye adınız ("${currentUser.fullName.toUpperCase()}") uyuşmamaktadır! Dernek tüzüğü ve kara para aklamayı önleme (AML) mali güvenlik ilkeleri gereğince başkası adına bağış yapılamaz ve işlem engellenir.`;
      setSecurityErrorMsg(mismatchMsg);
      if (onShowToast) {
        onShowToast('Güvenlik Uyarısı: Bağışçı adı ile onaylı üye adınız aynı olmalıdır!', 'warning', 'İşlem Engellendi');
      }
      return;
    }

    if (paymentMethod === 'pos') {
      if (!cardHolderName.trim()) {
        setSecurityErrorMsg('Kredi kartı üzerindeki ad ve soyadı girmeniz zorunludur.');
        return;
      }

      const normCardHolder = normalizeNameForComparison(cardHolderName);
      if (normCardHolder !== normMember) {
        const cardMismatchMsg = `🚨 GÜVENLİK İHLALİ VE ÖDEME REDDİ: Kredi kartı üzerindeki isim ("${cardHolderName.toUpperCase()}") ile sistemde kayıtlı onaylı üye adınız ("${currentUser.fullName.toUpperCase()}") uyuşmamaktadır! Dernek tüzüğü ve 3D Secure kart güvenliği protokolleri gereği farklı kişilere ait kartlarla ödeme kabul edilmez.`;
        setSecurityErrorMsg(cardMismatchMsg);
        if (onShowToast) {
          onShowToast('Güvenlik Reddi: Kart sahibi ismi ile onaylı üye ismi eşleşmedi!', 'warning', 'İşlem Reddedildi');
        }
        return;
      }

      setIsValidating(true);

      try {
        // SERVER-SIDE CROSS-REFERENCE VALIDATION ROUTE
        const response = await fetch('/api/donations/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            donorName: donorName,
            cardHolderName: cardHolderName,
            memberFullName: currentUser.fullName,
            memberId: currentUser.memberNo || currentUser.id,
            amount: finalAmount,
            paymentMethod: 'pos',
          }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const alertMsg = data.message || `🚨 GÜVENLİK İHLALİ VE SUNUCU REDDİ: Sunucu denetiminde isim eşleşmesi reddedildi!`;
          setSecurityErrorMsg(alertMsg);
          if (onShowToast) {
            onShowToast('Sunucu Güvenlik Reddi: İsim eşleşmesi doğrulanamadı!', 'warning', 'İşlem Reddedildi');
          }
          setIsValidating(false);
          return;
        }

        setIsValidating(false);

        // Launch 3D Secure POS Verification Modal upon server validation success
        setIs3DSecureModalOpen(true);
        setPosStep('processing');
        setTimeout(() => {
          setPosStep('otp');
        }, 1000);
      } catch (err) {
        console.error('Server side donation validation error:', err);
        setSecurityErrorMsg('🚨 SUNUCU HATA UYARISI: Kart sahibi ismi sunucu tarafında doğrulanırken bağlantı hatası oluştu. Lütfen tekrar deneyiniz.');
        setIsValidating(false);
      }
    } else {
      // Direct SEPA IBAN Transfer confirmation with server validation
      setIsValidating(true);
      try {
        const response = await fetch('/api/donations/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            donorName: donorName,
            memberFullName: currentUser.fullName,
            memberId: currentUser.memberNo || currentUser.id,
            amount: finalAmount,
            paymentMethod: 'sepa',
          }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const alertMsg = data.message || `🚨 GÜVENLİK İHLALİ VE SUNUCU REDDİ: Sunucu denetiminde isim eşleşmesi reddedildi!`;
          setSecurityErrorMsg(alertMsg);
          if (onShowToast) {
            onShowToast('Sunucu Güvenlik Reddi: İsim eşleşmesi doğrulanamadı!', 'warning', 'İşlem Reddedildi');
          }
          setIsValidating(false);
          return;
        }

        setIsValidating(false);
        processDonationSuccess(finalAmount, 'SEPA / IBAN Transferi');
      } catch (err) {
        console.error('Server side donation validation error:', err);
        setSecurityErrorMsg('🚨 SUNUCU HATA UYARISI: Sunucu tarafı isim doğrulaması yapılırken bağlantı hatası oluştu. Lütfen tekrar deneyiniz.');
        setIsValidating(false);
      }
    }
  };

  const processDonationSuccess = async (amountVal: number, methodLabel: any, mandateNo?: string) => {
    const newDonationRecord: DonationRecord = {
      id: 'don-' + Date.now(),
      donorName: donorName || currentUser?.fullName || 'Giriş Yapmış Üye',
      donorEmail: donorEmail || currentUser?.email || 'bagisci@kurdishculture.org',
      amount: amountVal,
      currency: 'EUR',
      campaign: campaign,
      date: new Date().toISOString().split('T')[0],
      paymentMethod: methodLabel,
      receiptNo: `RCP-2026-${Math.floor(Math.random() * 8999 + 1000)}`,
      isRecurring: donationType === 'recurring',
      frequency: donationType === 'recurring' ? 'Monthly' : undefined,
      ibanLast4: associationIban.replace(/\s+/g, '').slice(-4),
      mandateId: mandateNo || `POS-2026-${Math.floor(Math.random() * 89999 + 10000)}`,
      debitDayOfMonth: debitDayOfMonth,
      status: 'Active',
      verificationStatus: 'Pending_Admin_Approval',
      memberFullNameMatched: currentUser?.fullName,
      cardHolderNameMatched: paymentMethod === 'pos' ? cardHolderName : undefined,
      verificationNotes: 'GÜVENLİ BAĞIŞ DOĞRULAMASI: Sunucu kimlik kontrolü yapıldı (%100 Onaylı Üye Eşleşti). Yönetim Kurulu finansal nihai onayı bekleniyor.',
      verifiedAt: new Date().toISOString(),
    };

    try {
      const res = await createDonationApi(newDonationRecord);
      if (res.receiptNo) {
        newDonationRecord.receiptNo = res.receiptNo;
      }
    } catch (err) {
      console.error('Donation save error:', err);
    }

    if (onDonationSuccess) {
      onDonationSuccess(newDonationRecord);
    }

    if (onShowToast) {
      onShowToast(
        paymentMethod === 'pos'
          ? `€${amountVal} tutarındaki online POS ödemeniz / bağışınız kaydedildi! (Yönetim Onayı Bekliyor)`
          : `€${amountVal} tutarındaki SEPA IBAN transfer talebiniz kaydedildi! (Yönetim Onayı Bekliyor)`,
        'success',
        'İşlem Başarılı'
      );
    }

    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 },
    });

    setIsSuccess(true);
  };

  // 🔒 SECURITY LOCK: Non-members / visitors cannot access the donation portal
  if (!currentUser) {
    return (
      <div className="space-y-8 pb-16 max-w-3xl mx-auto px-4">
        {/* Top Banner */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-950/80 text-red-400 text-xs font-black uppercase tracking-wider border border-red-500/40 shadow-xl animate-pulse">
            <Lock className="w-4 h-4 text-red-400" />
            <span>Güvenlik Kilitli Bölüm • Yalnızca Üyelere Özel Bağış Portalı</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white font-heading">
            Bağış ve Ödeme Portalı Kilitlidir
          </h1>
          <p className="text-sm text-gray-200 max-w-xl mx-auto leading-relaxed">
            Kürt Kültür ve Dayanışma Derneği tüzüğü ve uluslararası finansal güvenlik protokolü gereğince, bağış ve ödeme menüsü <strong>yalnızca dernek yönetim kurulu tarafından kimliği doğrulanmış resmi üyelere açıktır.</strong>
          </p>
        </div>

        {/* Main Lock Card */}
        <div className="glass-card rounded-3xl p-6 sm:p-8 border-2 border-red-500/40 bg-[#0B1426]/95 space-y-6 shadow-[0_0_50px_rgba(220,38,38,0.25)]">
          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-start gap-4">
            <div className="p-3 rounded-2xl bg-red-500/20 text-red-400 shrink-0 border border-red-500/40">
              <ShieldAlert className="w-8 h-8" />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-base font-extrabold text-red-200">
                Dernek Güvenlik & Kimlik Doğrulama Kuralı
              </h3>
              <p className="text-xs text-gray-200 leading-relaxed">
                Ödeme ve bağış işlemlerinin onaylanabilmesi için <strong>kredi kartı üzerindeki isim ile dernek yönetim kurulu onaylı üyenin ad-soyadının %100 birebir aynı olması zorunludur.</strong> Kimliği doğrulanmamış veya üçüncü şahıs kartlarıyla yapılan işlemler sistem tarafından otomatik olarak <strong>REDDEDİLİR.</strong>
              </p>
            </div>
          </div>

          {/* Feature / Requirement Steps */}
          <div className="space-y-3">
            <p className="text-xs font-bold text-[#D4AF37] uppercase tracking-wide">
              Bağış Yapabilmek İçin Gerekli 3 Güvenlik Adımı:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-1.5">
                <div className="w-7 h-7 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] font-bold flex items-center justify-center text-sm">1</div>
                <p className="font-bold text-white">1. Üye Girişi / Kayıt</p>
                <p className="text-[11px] text-gray-300">Dernek üye portalına T.C./Pasaport veya SMS OTP ile giriş yapın veya kayıt oluşturun.</p>
              </div>

              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-1.5">
                <div className="w-7 h-7 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] font-bold flex items-center justify-center text-sm">2</div>
                <p className="font-bold text-white">2. Yönetim Kimlik Onayı</p>
                <p className="text-[11px] text-gray-300">Resmi kimlik bilgilerinizin yönetim kurulu tarafından onaylandığından emin olun.</p>
              </div>

              <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-1.5">
                <div className="w-7 h-7 rounded-lg bg-[#D4AF37]/20 text-[#D4AF37] font-bold flex items-center justify-center text-sm">3</div>
                <p className="font-bold text-white">3. Birebir Aynı İsimli Kart</p>
                <p className="text-[11px] text-gray-300">Ödeme yapacağınız kredi kartının üzerindeki ad soyad ile üye adınız %100 eşleşmelidir.</p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="pt-2 text-center space-y-3 border-t border-white/10">
            <button
              onClick={() => onOpenAuthModal && onOpenAuthModal()}
              className="btn-primary w-full sm:w-auto px-10 py-4 text-sm font-extrabold flex items-center justify-center gap-2 cursor-pointer shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              <User className="w-5 h-5" />
              <span>Üye Girişi Yap / Üye Ol (Bağış Portalı Kilit Aç)</span>
            </button>
            <p className="text-[11px] text-gray-400">
              Onaylı üye hesabınızla giriş yaptıktan sonra bağış ve ödeme formu erişime açılacaktır.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-16 max-w-4xl mx-auto">
      {/* VERIFIED MEMBER IDENTITY BADGE */}
      <div className="p-4 rounded-2xl bg-[#0B3D2E] border-2 border-[#D4AF37] text-white flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40 flex items-center justify-center shrink-0">
            <BadgeCheck className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-extrabold text-[#D4AF37] uppercase tracking-wider">YÖNETİM KURULU ONAYLI ÜYE KİMLİĞİ</span>
            </div>
            <p className="text-base font-black text-white mt-0.5">
              {currentUser.fullName} <span className="text-xs text-emerald-300 font-mono">({currentUser.memberNo})</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-black/30 px-3.5 py-1.5 rounded-xl border border-emerald-500/40 text-xs font-bold text-emerald-300">
          <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>Kart İsim Doğrulama Aktif</span>
        </div>
      </div>

      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#0B3D2E] text-[#D4AF37] text-xs font-bold uppercase tracking-wider border border-[#D4AF37]/30 shadow-md">
          <HeartHandshake className="w-4 h-4 text-[#D4AF37]" />
          <span>Bağış Yap & Destek Ol</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          Derneğimize Bağış Yapın
        </h1>
        <p className="text-sm sm:text-base text-gray-200 max-w-2xl mx-auto leading-relaxed">
          Kültür, dil ve toplumsal dayanışma çalışmalarımıza dernek <strong>Sanal POS Cihazı (Kredi / Banka Kartı)</strong> veya belirlediğimiz <strong>SEPA IBAN numarasına</strong> doğrudan para yatırarak destek olabilirsiniz.
        </p>
      </div>

      {/* INFORMATION BANNER */}
      <div className="glass-card rounded-2xl border border-[#D4AF37]/40 p-4 sm:p-5 bg-[#0B3D2E]/90 text-xs space-y-3 shadow-xl">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsInfoGuideOpen(!isInfoGuideOpen)}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-white text-sm">Dernek Sanal POS & SEPA Ödeme Bilgilendirmesi</p>
              <p className="text-gray-300 text-[11px]">Dernek Sanal POS Ödeme Altyapısı & SEPA Banka Hesabı</p>
            </div>
          </div>
          <button className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-[#D4AF37] transition-colors">
            {isInfoGuideOpen ? <ChevronDown className="w-5 h-5 rotate-180 transition-transform" /> : <ChevronDown className="w-5 h-5 transition-transform" />}
          </button>
        </div>

        {isInfoGuideOpen && (
          <div className="pt-3 border-t border-white/10 space-y-3 text-gray-200 leading-relaxed animate-in fade-in duration-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-xl bg-[#081B16] border border-white/10 space-y-1.5">
                <p className="font-bold text-[#D4AF37] flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
                  <CreditCard className="w-3.5 h-3.5 text-emerald-400" />
                  1. Dernek Sanal POS Cihazı
                </p>
                <p className="text-[11px] text-gray-300">
                  Kredi ve banka kartlarınızla doğrudan dernek sanal POS cihazı üzerinden ödemelerinizi gerçekleştirebilirsiniz.
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-[#081B16] border border-white/10 space-y-1.5">
                <p className="font-bold text-[#D4AF37] flex items-center gap-1.5 text-[11px] uppercase tracking-wide">
                  <Building className="w-3.5 h-3.5 text-emerald-400" />
                  2. Resmi SEPA IBAN Numarası
                </p>
                <p className="text-[11px] text-gray-300">
                  Belirlediğimiz dernek resmi SEPA IBAN hesabımıza banka uygulamanızdan doğrudan havale / EFT gerçekleştirebilirsiniz.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {isSuccess ? (
        /* SUCCESS SCREEN WITH ADMIN APPROVAL NOTICE */
        <div className="glass-card rounded-3xl p-8 sm:p-12 border-2 border-[#D4AF37] text-center space-y-6 shadow-2xl animate-in zoom-in-95 duration-300">
          <div className="w-20 h-20 rounded-full bg-[#0B3D2E] text-[#D4AF37] border-2 border-[#D4AF37] mx-auto flex items-center justify-center shadow-lg">
            <CheckCircle2 className="w-10 h-10" />
          </div>

          <div className="space-y-2 max-w-xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading">
              Bağış İşleminiz Kaydedildi!
            </h2>
            <p className="text-sm text-emerald-300 font-semibold">
              🛡️ Güvenli Bağış Doğrulama Protokolü Başarıyla Uygulandı
            </p>
            <p className="text-xs text-gray-300 leading-relaxed">
              Kullanıcı adınız, bağışçı adınız ve ödeme bilgileriniz sunucu tarafında %100 doğrulanmıştır. İşleminiz Dernek Yönetim Kurulu denetimine sunulmuştur.
            </p>
          </div>

          {/* SECURE VERIFICATION STATUS BADGE */}
          <div className="p-4 rounded-2xl bg-[#0B3D2E] border border-[#D4AF37]/50 max-w-md mx-auto text-left space-y-2 text-xs text-white shadow-xl">
            <div className="flex items-center gap-2 font-extrabold text-[#D4AF37] border-b border-white/10 pb-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
              <span>GÜVENLİ BAĞIŞ DOĞRULAMA DURUMU</span>
            </div>
            <div className="flex justify-between items-center pt-1">
              <span className="text-gray-300">Onaylı Üye Adı:</span>
              <span className="font-bold text-white">{currentUser?.fullName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300">İsim Eşleşme Skoru:</span>
              <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-mono text-[10px] font-bold">
                ✓ %100 Eşleşti
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Yönetim Onayı:</span>
              <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 font-mono text-[10px] font-bold flex items-center gap-1">
                <Clock className="w-3 h-3 animate-spin" />
                ⏳ Yönetim Kurulu Onayı Bekliyor
              </span>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-[#081B16] border border-white/10 max-w-md mx-auto text-left space-y-2.5 text-xs text-gray-300 shadow-inner">
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">Bağışçı / Ödeyen:</span>
              <span className="font-bold text-white">{donorName || currentUser?.fullName}</span>
            </div>
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">İşlem Tutarı:</span>
              <span className="font-extrabold text-[#D4AF37]">€{customAmount || selectedAmount}</span>
            </div>
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">Fon / Kampanya:</span>
              <span className="font-medium text-gray-200">{campaign}</span>
            </div>
            <div className="flex justify-between border-b border-white/10 pb-2">
              <span className="text-gray-400">Ödeme Yöntemi:</span>
              <span className="font-bold text-emerald-400">
                {paymentMethod === 'pos' ? 'Dernek Sanal POS Cihazı' : 'Dernek SEPA IBAN Transferi'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Makbuz Kodu:</span>
              <span className="font-mono font-bold text-[#D4AF37]">RCP-2026-{Math.floor(Math.random()*8999 + 1000)}</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => alert('PDF Geçici Bağış Makbuzu indirildi. Yönetim Kurulu onayının ardından resmi imzalı nüsha e-postanıza iletilecektir.')}
              className="btn-primary"
            >
              <Download className="w-4 h-4" />
              <span>Geçici Makbuz İndir (PDF)</span>
            </button>
            <button
              onClick={() => {
                setIsSuccess(false);
              }}
              className="btn-secondary"
            >
              <span>Yeni Bağış Yap</span>
            </button>
          </div>
        </div>
      ) : (
        /* MAIN DONATION FORM */
        <div className="glass-card rounded-3xl p-6 sm:p-10 border border-white/10 shadow-2xl space-y-8">
          
          {/* STEP 1: PAYMENT METHOD SELECTOR (SANAL POS VS SEPA IBAN) */}
          <div className="space-y-3">
            <label className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider block">
              1. Ödeme / Bağış Yöntemini Seçin
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setPaymentMethod('pos')}
                className={`p-5 rounded-2xl border text-left flex items-start gap-4 transition-all relative overflow-hidden ${
                  paymentMethod === 'pos'
                    ? 'bg-[#0B3D2E] text-white border-[#D4AF37] ring-2 ring-[#D4AF37]/50 shadow-xl'
                    : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center shrink-0 mt-0.5">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-white">Dernek Sanal POS Cihazı</span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-extrabold uppercase border border-emerald-500/30">
                      Anında Onay
                    </span>
                  </div>
                  <p className="text-xs text-gray-300 mt-1 leading-snug">
                    Kredi kartı veya banka kartınızla online, 3D-Secure korumalı hızlı ödeme.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod('sepa')}
                className={`p-5 rounded-2xl border text-left flex items-start gap-4 transition-all ${
                  paymentMethod === 'sepa'
                    ? 'bg-[#0B3D2E] text-white border-[#D4AF37] ring-2 ring-[#D4AF37]/50 shadow-xl'
                    : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/20 text-[#D4AF37] flex items-center justify-center shrink-0 mt-0.5">
                  <Building className="w-5 h-5" />
                </div>
                <div>
                  <span className="font-bold text-sm text-white">Resmi SEPA / IBAN Transferi</span>
                  <p className="text-xs text-gray-300 mt-1 leading-snug">
                    Derneğimizin belirlediği SEPA IBAN hesabına doğrudan havale / EFT ile para yatırma.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* STEP 2: CAMPAIGN SELECTOR */}
          <div className="space-y-3">
            <label className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider block">
              2. Desteklemek İstediğiniz Fonu / Kampanyayı Seçin
            </label>
            <select
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              className="w-full p-3.5 rounded-2xl bg-[#081B16] border border-white/10 text-white text-sm focus:outline-none focus:border-[#D4AF37] font-medium cursor-pointer"
            >
              {campaignsList.map((c) => (
                <option key={c.id} value={c.title}>
                  {c.title} ({c.category || 'Genel Fon'})
                </option>
              ))}
            </select>

            {/* Campaign Detail Card */}
            {selectedCampaignObj && (
              <div className="p-4 rounded-2xl bg-[#081B16] border border-[#D4AF37]/30 space-y-2.5 animate-in fade-in duration-200">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white text-xs flex items-center gap-1.5">
                    <Info className="w-4 h-4 text-[#D4AF37]" />
                    <span>{selectedCampaignObj.title}</span>
                  </span>
                  <span className="px-2.5 py-0.5 rounded-md bg-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold border border-[#D4AF37]/30">
                    {selectedCampaignObj.category || 'Bağış Fonu'}
                  </span>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  {selectedCampaignObj.description}
                </p>
              </div>
            )}
          </div>

          {/* STEP 3: AMOUNT SELECTOR */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider block">
                3. Bağış Tutarı (EUR)
              </label>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
              {quickAmounts.map((amt) => (
                <button
                  type="button"
                  key={amt}
                  onClick={() => {
                    setSelectedAmount(amt);
                    setCustomAmount('');
                  }}
                  className={`py-3 rounded-2xl font-bold text-sm border transition-all ${
                    selectedAmount === amt && !customAmount
                      ? 'bg-[#0B3D2E] text-[#D4AF37] border-[#D4AF37] shadow-lg ring-1 ring-[#D4AF37]'
                      : 'bg-white/5 text-gray-300 border-transparent hover:bg-white/10'
                  }`}
                >
                  €{amt}
                </button>
              ))}
              <div className="relative">
                <input
                  type="number"
                  placeholder="Farklı Tutar"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full py-3 px-2 text-center rounded-2xl bg-[#081B16] border border-white/10 text-white text-xs font-bold focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>
          </div>

          {/* STEP 4: PAYMENT DETAILS (POS INPUTS OR SEPA IBAN DISPLAY) */}
          {paymentMethod === 'sepa' ? (
            /* SEPA IBAN DISPLAY AREA */
            <div className="p-5 rounded-2xl bg-[#0B3D2E] border-2 border-[#D4AF37] space-y-4 text-xs text-white shadow-xl animate-in fade-in duration-300">
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <span className="font-extrabold text-[#D4AF37] text-sm flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#D4AF37]" />
                  <span>Resmi Dernek SEPA IBAN Transfer Hesabı</span>
                </span>
                <span className="bg-emerald-500/20 text-emerald-300 text-[10px] px-2.5 py-1 rounded font-bold border border-emerald-500/40">
                  🔒 Doğrudan Resmi Dernek Hesabı
                </span>
              </div>

              <p className="text-xs text-gray-200 leading-relaxed">
                Aşağıda belirtilen dernek resmi SEPA IBAN hesabımıza bankanızın mobil uygulamasından doğrudan para yatırabilir veya transfer yapabilirsiniz:
              </p>

              <div className="p-4 rounded-xl bg-[#081B16] border border-white/15 space-y-3 font-mono text-xs">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 border-b border-white/10 pb-2">
                  <span className="text-gray-400">Dernek Unvanı:</span>
                  <strong className="text-white font-bold">{associationAccountName}</strong>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 border-b border-white/10 pb-2">
                  <span className="text-gray-400">Resmi SEPA IBAN:</span>
                  <div className="flex items-center gap-2">
                    <strong className="text-[#D4AF37] font-extrabold text-sm">{associationIban}</strong>
                    <button
                      type="button"
                      onClick={handleCopyIban}
                      className="px-2.5 py-1 rounded bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] text-[10px] font-bold border border-[#D4AF37]/40 flex items-center gap-1 transition-colors"
                    >
                      {copiedIban ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                      <span>{copiedIban ? 'Kopyalandı' : 'Kopyala'}</span>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 border-b border-white/10 pb-2">
                  <span className="text-gray-400">SWIFT / BIC Kodu:</span>
                  <strong className="text-emerald-300 font-bold">{associationSwift}</strong>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                  <span className="text-gray-400">Açıklama / Referans Kodu:</span>
                  <strong className="text-amber-200 font-bold bg-amber-500/20 px-2.5 py-1 rounded border border-amber-500/30">
                    SEPA-BAGIS-2026
                  </strong>
                </div>
              </div>

              <p className="text-[11px] text-emerald-200/90 italic">
                * Açıklama kısmına adınızı ve "Bağış" kelimesini yazmanız makbuzunuzun otomatik düzenlenmesini sağlar.
              </p>
            </div>
          ) : (
            /* SANAL POS KREDİ KARTI FORMU */
            <div className="space-y-4 p-5 rounded-2xl bg-[#081B16] border border-white/15 animate-in fade-in duration-300">
              <div className="flex items-center gap-2 text-xs font-bold text-[#D4AF37] border-b border-white/10 pb-3">
                <CreditCard className="w-4 h-4 text-emerald-400" />
                <span>Dernek Online Sanal POS Cihazı Ödeme Ekranı</span>
              </div>

              <div>
                <label className="text-xs text-gray-200 font-bold block mb-1">
                  Kart Üzerindeki Ad Soyad * <span className="text-amber-300 font-normal text-[11px]">(ZORUNLU: Kimlik doğrulamasındaki isim ile birebir aynı olmak zorundadır)</span>
                </label>
                <input
                  type="text"
                  required
                  value={cardHolderName}
                  onChange={(e) => setCardHolderName(e.target.value)}
                  placeholder="Kart üzerindeki ad soyad"
                  className={`w-full p-3.5 rounded-xl bg-[#0B3D2E] border text-white text-xs font-bold focus:outline-none transition-all ${
                    currentUser && normalizeNameForComparison(cardHolderName) !== normalizeNameForComparison(currentUser.fullName)
                      ? 'border-red-500 ring-2 ring-red-500/50'
                      : 'border-white/20 focus:border-[#D4AF37]'
                  }`}
                />
                {cardHolderName && currentUser && normalizeNameForComparison(cardHolderName) !== normalizeNameForComparison(currentUser.fullName) && (
                  <div className="p-3 rounded-xl bg-red-950/90 border border-red-500/80 text-red-100 text-xs flex items-start gap-2 mt-2 shadow-lg animate-in fade-in duration-200">
                    <ShieldAlert className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-extrabold text-red-300 block mb-0.5">🚨 ÖDEME ENGELİ: KART İSMİ İLE KİMLİK İSMİ UYUMSUZ!</span>
                      <span>
                        Kart üzerindeki ad-soyad (<strong>"{cardHolderName.toUpperCase()}"</strong>) ile üyeliğinize ait kimlik doğrulamasındaki isim (<strong>"{currentUser.fullName.toUpperCase()}"</strong>) uyum sağlamıyor! Güvenlik nedeniyle ödeme engellendi. Kart sahibi ismi ile kimlik doğrulamasındaki isim aynı olmak zorundadır.
                      </span>
                    </div>
                  </div>
                )}
                {cardHolderName && currentUser && normalizeNameForComparison(cardHolderName) === normalizeNameForComparison(currentUser.fullName) && (
                  <div className="p-2 rounded-lg bg-emerald-950/60 border border-emerald-500/50 text-emerald-300 text-[11px] flex items-center gap-1.5 mt-1.5 font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span>✓ Üye Kimlik Doğrulaması İle Kart İsmi Tam Uyumlu (%100 Onaylı)</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-gray-300 font-medium block mb-1">Kart Numarası *</label>
                <input
                  type="text"
                  required
                  maxLength={19}
                  value={cardNumber}
                  onChange={(e) => setCardNumber(e.target.value)}
                  placeholder="4543 •••• •••• 3312"
                  className="w-full p-3.5 rounded-xl bg-[#0B3D2E] border border-white/20 text-white text-xs focus:outline-none focus:border-[#D4AF37] font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-300 font-medium block mb-1">Son Kullanma (AA/YY) *</label>
                  <input
                    type="text"
                    required
                    placeholder="12/28"
                    value={cardExp}
                    onChange={(e) => setCardExp(e.target.value)}
                    className="w-full p-3.5 rounded-xl bg-[#0B3D2E] border border-white/20 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-300 font-medium block mb-1">CVC / CVV Kodu *</label>
                  <input
                    type="text"
                    required
                    maxLength={4}
                    placeholder="321"
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value)}
                    className="w-full p-3.5 rounded-xl bg-[#0B3D2E] border border-white/20 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
                  />
                </div>
              </div>
            </div>
          )}

          {/* DONOR INFORMATION FORM */}
          <form onSubmit={handleDonateSubmit} className="space-y-4 pt-2">

            {/* SECURITY ERROR BANNER */}
            {securityErrorMsg && (
              <div className="p-4 rounded-2xl bg-red-950/90 border-2 border-red-500 text-red-100 text-xs space-y-1.5 shadow-2xl animate-in zoom-in-95">
                <div className="flex items-center gap-2 font-extrabold text-red-400 text-sm">
                  <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
                  <span>KART VE KİMLİK BİLGİSİ UYARISI</span>
                </div>
                <p className="leading-relaxed text-red-100 font-medium">{securityErrorMsg}</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-300 font-bold block mb-1">
                  Bağışçı / Ödeyen Ad Soyad *
                </label>
                <input
                  type="text"
                  required
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder="Tam adınız"
                  className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 font-medium block mb-1">E-Posta Adresi (Makbuz Gönderimi) *</label>
                <input
                  type="email"
                  required
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="deniz@example.com"
                  className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-300 font-medium block mb-1">Telefon Numarası *</label>
                <input
                  type="tel"
                  required
                  value={donorPhone}
                  onChange={(e) => setDonorPhone(e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                  className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
                />
              </div>

              <div>
                <label className="text-xs text-gray-300 font-medium block mb-1">T.C. / Pasaport No (Opsiyonel)</label>
                <input
                  type="text"
                  value={identityNumber}
                  onChange={(e) => setIdentityNumber(e.target.value)}
                  placeholder="38291029184"
                  className="w-full p-3 rounded-xl bg-[#081B16] border border-white/10 text-white text-xs focus:outline-none focus:border-[#D4AF37]"
                />
              </div>
            </div>

            {/* SUBMIT BUTTON */}
            <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/10">
              <div className="flex items-center gap-2 text-xs text-emerald-400 font-medium">
                <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                <span>Güvenli Sanal POS & SEPA Ödeme Altyapısı</span>
              </div>

              <button 
                type="submit" 
                disabled={isValidating}
                className="btn-primary w-full sm:w-auto px-8 py-4 text-sm font-extrabold flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isValidating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin text-[#D4AF37]" />
                    <span>Sunucu Kimlik Doğrulaması Yapılıyor...</span>
                  </>
                ) : paymentMethod === 'pos' ? (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>€{customAmount || selectedAmount} Online POS Ödemesi Yap</span>
                  </>
                ) : (
                  <>
                    <Building className="w-5 h-5" />
                    <span>€{customAmount || selectedAmount} SEPA IBAN Transferini Bildir</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* SANAL POS 3D SECURE VERIFICATION MODAL */}
      {is3DSecureModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-[#0F2D24] border-2 border-[#D4AF37] rounded-3xl shadow-2xl overflow-hidden flex flex-col text-white">
            
            {/* Modal Header */}
            <div className="p-5 bg-[#0B3D2E] flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] flex items-center justify-center font-bold text-xs">
                  3D
                </div>
                <div>
                  <p className="font-extrabold text-sm tracking-wide">3D Secure Kart Onayı</p>
                  <p className="text-[10px] text-emerald-300">Dernek Sanal POS Güvenlik Geçidi</p>
                </div>
              </div>
              <button
                onClick={() => setIs3DSecureModalOpen(false)}
                className="p-1.5 rounded-full bg-black/20 hover:bg-black/40 text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">

              {posStep === 'processing' && (
                <div className="py-10 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full border-4 border-t-[#D4AF37] border-white/10 animate-spin mx-auto flex items-center justify-center">
                    <Lock className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-bold text-white text-base">Sanal POS Provizyonu Alınıyor...</p>
                    <p className="text-xs text-gray-300">Kart provizyon doğrulaması yapılıyor.</p>
                  </div>
                </div>
              )}

              {posStep === 'otp' && (
                <div className="space-y-5 animate-in fade-in duration-300 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/20 border border-[#D4AF37] text-[#D4AF37] mx-auto flex items-center justify-center">
                    <Smartphone className="w-7 h-7" />
                  </div>

                  <div className="space-y-1">
                    <p className="font-bold text-white text-base">SMS Onay Kodu</p>
                    <p className="text-xs text-gray-300">
                      <strong>{donorPhone}</strong> numaralı telefonunuza gönderilen 3D Secure onay kodunu giriniz.
                    </p>
                  </div>

                  <div className="max-w-xs mx-auto space-y-2">
                    <input
                      type="text"
                      maxLength={6}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      placeholder="849201"
                      className="w-full p-3.5 text-center text-xl font-bold font-mono tracking-widest rounded-2xl bg-[#081B16] border-2 border-[#D4AF37] text-white focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setOtpCode('849201')}
                      className="text-[11px] text-[#D4AF37] hover:underline block mx-auto font-medium"
                    >
                      (Kodu Otomatik Doldur: 849201)
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      const finalAmount = customAmount ? parseFloat(customAmount) : selectedAmount;
                      setIs3DSecureModalOpen(false);
                      processDonationSuccess(finalAmount, 'Sanal POS (Kredi Kartı)');
                    }}
                    className="w-full py-3.5 rounded-xl bg-[#D4AF37] text-[#081B16] font-extrabold text-xs flex items-center justify-center gap-2 hover:bg-yellow-400 transition-colors shadow-lg"
                  >
                    <span>Ödemeyi Onayla ve Bağışı Tamamla</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
};
